import { config } from '@/config'
import { apiErrorMessage } from '@/lib/errors'

const AUTH_TOKEN_KEY = 'idToken'

let authToken: string | null = null

export function setAuthToken(token: string | null) {
  authToken = token
}

export function getAuthToken(): string | null {
  return authToken
}

export function clearPersistedAuth() {
  setAuthToken(null)
  localStorage.removeItem(AUTH_TOKEN_KEY)
  localStorage.removeItem('accessToken')
}

export class ApiError extends Error {
  status: number
  code: string
  detail?: unknown

  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
  }
}

type QueryValue = string | number | boolean | undefined | null | string[] | number[]

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  params?: Record<string, QueryValue>
}

async function refreshAuthToken(): Promise<string | null> {
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth')
    const { configureAmplify, isCognitoConfigured } = await import('@/lib/amplify')
    if (!isCognitoConfigured()) return null
    configureAmplify()
    const session = await fetchAuthSession({ forceRefresh: true })
    const token = session.tokens?.idToken?.toString()
    if (!token) return null
    setAuthToken(token)
    localStorage.setItem(AUTH_TOKEN_KEY, token)
    return token
  } catch {
    return null
  }
}

async function request<T>(
  endpoint: string,
  options: RequestOptions & { _authRetried?: boolean } = {},
): Promise<T> {
  const { body, params, _authRetried = false, ...rest } = options

  let url = `${config.api.baseUrl}${endpoint}`
  if (params) {
    const searchParams = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') continue
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item !== undefined && item !== null && item !== '') searchParams.append(key, String(item))
        }
        continue
      }
      searchParams.append(key, String(value))
    }
    const qs = searchParams.toString()
    if (qs) url += `?${qs}`
  }

  const headers: Record<string, string> = {
    ...(rest.headers as Record<string, string>),
  }

  if (authToken || localStorage.getItem(AUTH_TOKEN_KEY)) {
    authToken = authToken || localStorage.getItem(AUTH_TOKEN_KEY)
    headers.Authorization = `Bearer ${authToken}`
  }

  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(url, {
    ...rest,
    headers,
    body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    if (response.status === 401 && !_authRetried) {
      const refreshed = await refreshAuthToken()
      if (refreshed) {
        return request<T>(endpoint, { ...options, _authRetried: true })
      }
    }

    let errorData: { detail?: unknown; message?: string } = {}
    try {
      errorData = await response.json()
    } catch {
      // ignore
    }

    const message = apiErrorMessage(response.status, errorData.detail, errorData.message)

    throw new ApiError(response.status, `HTTP_${response.status}`, message, errorData.detail)
  }

  if (response.status === 204) return undefined as T

  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    return (await response.json()) as T
  }
  return (await response.blob()) as T
}

export const api = {
  get: <T>(endpoint: string, params?: RequestOptions['params']) =>
    request<T>(endpoint, { method: 'GET', params }),

  post: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, { method: 'POST', body }),

  patch: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, { method: 'PATCH', body }),

  del: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),

  getBlob: (endpoint: string, params?: RequestOptions['params']) =>
    request<Blob>(endpoint, { method: 'GET', params }),
}

export interface AccSumRow {
  trade_date: string
  book: string
  login_id: number
  prev_balance?: number | string | null
  margin_in?: number | string | null
  margin_out?: number | string | null
  adjust?: number | string | null
  commission?: number | string | null
  interest?: number | string | null
  trading_pl?: number | string | null
  new_balance?: number | string | null
  floating_pl?: number | string | null
  credit_in?: number | string | null
  credit_out?: number | string | null
  total_credit?: number | string | null
  equity?: number | string | null
  today_lot?: number | string | null
  monthly_lot?: number | string | null
  monthly_in_out?: number | string | null
  accumulation_in_out?: number | string | null
  daily_pl?: number | string | null
  ib_team?: string | null
  rate?: number | string | null
  type_akun?: string | null
  updated_at?: string | null
}

export interface AccSumListResponse {
  items: AccSumRow[]
  total: number
  page: number
  page_size: number
  trade_date: string
  book: string
}

export interface BookStatus {
  book_id: string
  platform: string
  enabled: boolean
  reason?: string
}

export const backofficeApi = {
  listBooks: () => api.get<BookStatus[]>('/api/v1/backoffice/books'),

  listAccSum: (params: {
    trade_date: string
    book: string
    page?: number
    page_size?: number
    login_id?: number
    sort?: string
    order?: string
  }) => api.get<AccSumListResponse>('/api/v1/backoffice/accsum', params),

  exportAccSum: (params: { trade_date: string; book: string }) =>
    api.getBlob('/api/v1/backoffice/accsum/export.xlsx', params),

  listUsers: () => api.get<{ items: BackofficeUser[] }>('/api/v1/backoffice/users'),

  createUser: (body: { email: string; role: string }) =>
    api.post<BackofficeUserCreateResponse>('/api/v1/backoffice/users', body),

  updateUser: (username: string, body: { role?: string; enabled?: boolean }) =>
    api.patch<BackofficeUser>(
      `/api/v1/backoffice/users/${encodeURIComponent(username)}`,
      body,
    ),

  resetUserPassword: (username: string) =>
    api.post<BackofficePasswordResetResponse>(
      `/api/v1/backoffice/users/${encodeURIComponent(username)}/reset-password`,
    ),

  deskSummary: (scope: 'opening' | 'settlement') =>
    api.get<DeskSummary>(`/api/v1/backoffice/${scope}/summary`),

  openingBook: (params: DeskListParams) =>
    api.get<Page<AgreementRow>>('/api/v1/backoffice/opening/agreements', params),

  openingFilterOptions: () => api.get<AgreementFilterOptions>('/api/v1/backoffice/opening/agreements/filter-options'),

  openingDetail: (clientId: string) =>
    api.get<OpeningDetail>(`/api/v1/backoffice/opening/agreements/${clientId}`),

  openingPatch: (clientId: string, body: { risk_band?: string; kyc_tier?: string }) =>
    api.patch<AgreementRow>(`/api/v1/backoffice/opening/agreements/${clientId}`, body),

  openingAction: (clientId: string, action: 'approve' | 'reject' | 'request-info', notes?: string) =>
    api.post(`/api/v1/backoffice/opening/agreements/${clientId}/${action}`, { notes }),

  fundsBook: (
    params: DeskListParams & {
      kind?: string | string[]
      kind_mode?: string
      first_only?: boolean
      confirmed_from?: string
      confirmed_to?: string
    },
  ) => api.get<Page<FundQueueItem>>('/api/v1/backoffice/settlement/funds', params),

  dailyFunds: (date_from: string, date_to: string) =>
    api.get<{ items: DailyFundsRow[] }>('/api/v1/backoffice/settlement/reports/daily-funds', { date_from, date_to }),

  dailyAccsum: (book: string, date_from: string, date_to: string) =>
    api.get<{ items: DailyAccsumRow[] }>('/api/v1/backoffice/reports/daily-accsum', { book, date_from, date_to }),

  fundsDecide: (id: string, approve: boolean, unmatched_reason?: string) =>
    api.post(`/api/v1/backoffice/settlement/funds/${id}/decide`, { approve, unmatched_reason }),

  importBcaCsv: (csv_text: string) =>
    api.post<{ imported: number; skipped_duplicates: number }>('/api/v1/backoffice/settlement/bca/csv', { csv_text }),

  bcaList: (params: { page?: number; page_size?: number; sort?: string; order?: string }) =>
    api.get<Page<BcaRow>>('/api/v1/backoffice/settlement/bca', params),

  listBanks: () => api.get<CompanyBank[]>('/api/v1/backoffice/settlement/company-banks'),

  // is_active omitted = inactive on create, unchanged on update.
  saveBank: (body: Omit<CompanyBank, 'id' | 'is_active'> & { id?: string; is_active?: boolean }) => {
    const qs = body.id ? `?bank_id=${encodeURIComponent(body.id)}` : ''
    return api.post<{ id: string }>(`/api/v1/backoffice/settlement/company-banks${qs}`, body)
  },

  listRates: () => api.get<{ exchange_rates: number[]; items: RateRow[] }>('/api/v1/backoffice/settlement/rates'),

  addRate: (rate: number) => api.post<{ id: string; rate: number }>('/api/v1/backoffice/settlement/rates', { rate }),

  patchBank: (id: string, is_active: boolean) =>
    api.patch<{ id: string; is_active: boolean }>(`/api/v1/backoffice/settlement/company-banks/${id}`, { is_active }),

  patchRate: (id: string, is_active: boolean) =>
    api.patch(`/api/v1/backoffice/settlement/rates/${id}`, { is_active }),

  deleteBank: (id: string) => api.del<void>(`/api/v1/backoffice/settlement/company-banks/${id}`),

  deleteRate: (id: string) => api.del<void>(`/api/v1/backoffice/settlement/rates/${id}`),

  clientBanks: (params: { q?: string; account_type?: string; segment?: string; page?: number; page_size?: number }) =>
    api.get<Page<ClientBankRow>>('/api/v1/backoffice/settlement/client-banks', params),

  revealClientBankAccount: (accountId: string) =>
    api.post<{ account_number: string }>(`/api/v1/backoffice/settlement/client-banks/${accountId}/reveal`),

  listAccounts: (params: { q?: string; account_type?: string; page?: number; page_size?: number; sort?: string; order?: string }) =>
    api.get<Page<LiveAccount>>('/api/v1/backoffice/accounts', params),

  ekycLogs: (scope: 'opening' | 'settlement', params: DeskListParams) =>
    api.get<Page<EkycRow>>(
      scope === 'opening'
        ? '/api/v1/backoffice/opening/logs/ekyc'
        : '/api/v1/backoffice/settlement/logs/ekyc',
      params,
    ),

  activityLogs: (scope: 'opening' | 'settlement', params: ActivityParams) =>
    api.get<Page<ActivityRow>>(
      scope === 'opening'
        ? '/api/v1/backoffice/opening/logs/activity'
        : '/api/v1/backoffice/settlement/logs/activity',
      params,
    ),

  activityOptions: (scope: 'opening' | 'settlement') =>
    api.get<ActivityOptions>(
      scope === 'opening'
        ? '/api/v1/backoffice/opening/logs/activity/options'
        : '/api/v1/backoffice/settlement/logs/activity/options',
    ),
}

export interface DeskListParams {
  [key: string]: QueryValue | undefined
  status?: string | string[]
  status_mode?: string
  status_filter?: string
  q?: string
  page?: number
  page_size?: number
  include_total?: boolean
  utm_source?: string | string[]
  utm_source_mode?: string
  utm_medium?: string | string[]
  utm_medium_mode?: string
  utm_campaign?: string | string[]
  utm_campaign_mode?: string
  kyc_tier?: string | string[]
  kyc_tier_mode?: string
  risk_band?: string | string[]
  risk_band_mode?: string
}

export interface AgreementFilterOptions {
  statuses: string[]
  utm_sources: string[]
  utm_mediums: string[]
  utm_campaigns: string[]
  kyc_tiers: string[]
  risk_bands: string[]
}

export interface Page<T> {
  items: T[]
  total: number | null
  page: number
  page_size: number
  has_more?: boolean
}

export interface DeskSummary {
  pending_agreements: number
  pending_deposits: number
  pending_withdrawals: number
  processing_funds: number
  ekyc_pending: number
}

export interface AgreementRow {
  client_id: string
  user_id?: string
  client_code?: string | null
  full_name: string
  email: string
  status: string
  identity_number?: string | null
  mobile_phone?: string | null
  kyc_tier?: string | null
  risk_band?: string | null
  approved_by?: string | null
  utm_source?: string | null
  utm_medium?: string | null
  utm_campaign?: string | null
  created_at?: string | null
  updated_at?: string | null
  demo_login?: number | string | null
  live_login?: number | string | null
  ib_team?: string | null
  first_deposit_at?: string | null
}

export type OpeningQueueItem = AgreementRow

export interface OpeningDetail {
  client: {
    id: string
    full_name: string
    email: string
    mobile_phone?: string
    identity_number?: string
    identity_type?: string | null
    npwp_number?: string | null
    status: string
    client_code?: string | null
    birth_place?: string | null
    birth_date?: string | null
    gender?: string | null
    marital_status?: string | null
    mother_maiden_name?: string | null
    address_street?: string | null
    address_kelurahan?: string | null
    address_kecamatan?: string | null
    address_kota?: string | null
    address_provinsi?: string | null
    home_postal_code?: string | null
    nationality_code?: string | null
    home_ownership_status?: string | null
    account_purpose?: string | null
  }
  draft_data: Record<string, unknown>
  cdds: Array<{ form_code: string; ya_at: string; extra: Record<string, unknown>; body_snapshot?: unknown }>
  banks: Array<{ id: string; bank_name: string; account_number: string }>
}

export interface FundQueueItem {
  id: string
  client_id: string
  kind: string
  status: string
  amount: string
  currency?: string | null
  unmatched_reason?: string | null
  created_at: string
  confirmed_at?: string | null
  reference_code?: string | null
  transfer_method?: string | null
  mt_login?: string | null
  mt_ticket?: string | null
  full_name?: string | null
  client_code?: string | null
  ib_team?: string | null
}

export interface DailyFundsRow {
  date: string
  currency: string
  deposit_count: number
  deposit_sum: string
  withdraw_count: number
  withdraw_sum: string
  transfer_count: number
  transfer_sum: string
  net: string
}

export interface DailyAccsumRow {
  trade_date: string
  accounts: number
  margin_in: string
  margin_out: string
  trading_pl: string
  daily_pl: string
  equity: string
  today_lot: string
}

export interface BcaRow {
  id: string
  txn_date?: string | null
  amount?: string | null
  description?: string | null
  account_number?: string | null
  matched_request_id?: string | null
  imported_at?: string | null
}

export interface CompanyBank {
  id: string
  bank_name: string
  account_number: string
  account_name: string
  currency: string
  is_active: boolean
}

export interface ClientBankAccountItem {
  id: string
  bank_name: string
  branch: string
  account_holder: string
  account_number_masked: string
  account_type: 'GIRO' | 'TABUNGAN' | 'LAINNYA'
  is_primary: boolean
}

export interface ClientBankRow {
  client_id: string
  client_code: string
  full_name: string
  email: string
  segment: 'real' | 'demo' | 'test'
  accounts: ClientBankAccountItem[]
}

export interface RateRow {
  id: string
  rate: number
  is_active: boolean
  created_at?: string
}

export interface LiveAccount {
  login_id: number
  client_code: string
  account_type: string
  status: string
  leverage: number
  group: string
  last_sync_at?: string | null
  platform: string
  book: string
  ib_team?: string | null
}

export interface EkycRow {
  id: string
  client_id: string
  full_name?: string | null
  client_code?: string | null
  vendor: string
  status: string
  created_at?: string | null
}

export type ActivityParams = {
  page?: number
  page_size?: number
  sort?: string
  order?: string
  action?: string[] | string
  action_mode?: string
  actor_id?: string
  target_id?: string
  /** YYYY-MM-DD, Asia/Jakarta calendar day. */
  date_from?: string
  date_to?: string
}

/** Distinct values from the log itself; actors are labelled with their Cognito email. */
export interface ActivityOptions {
  actors: { value: string; label: string }[]
  actions: string[]
  target_types: string[]
}

export interface ActivityRow {
  id: string
  actor_id: string
  actor_email?: string | null
  action: string
  target_type: string
  target_id: string
  created_at?: string | null
}

export interface BackofficeUser {
  username: string
  sub: string
  email: string
  role: string
  status: string
  enabled: boolean
}

export interface BackofficeUserCreateResponse extends BackofficeUser {
  temporary_password: string
}

export interface BackofficePasswordResetResponse {
  username: string
  temporary_password: string
}
